/**
 * 检查提供的URL是否指向视频资源
 * @param url - 需要检查的URL
 * @returns boolean - 如果URL指向视频返回true，否则返回false
 */
export function isVideoUrl(url: string): boolean {
  if (!url) return false;

  // 检查常见视频文件扩展名
  const videoExtensions = [
    '.mp4',
    '.avi',
    '.mov',
    '.wmv',
    '.flv',
    '.mkv',
    '.webm',
    '.m4v',
    '.mpeg',
    '.mpg',
    '.3gp',
  ];
  const hasVideoExtension = videoExtensions.some((ext) => url.toLowerCase().endsWith(ext));

  return hasVideoExtension;
}

/**
 * 将字节转换为千字节(KB)
 * @param bytes - 字节数
 * @returns number - 转换后的KB值，向上取整为整数
 */
export function bytesToKB(bytes: number): number {
  if (bytes <= 0) return 0;
  return Math.ceil(bytes / 1024);
}

/**
 * 将千字节(KB)转换为兆字节(MB)
 * @param kb - 千字节数
 * @returns string - 转换后的MB值，整数或保留一位小数
 */
export function kbToMB(kb: number): string {
  if (kb <= 0) return '0';

  // 计算MB值
  const mb = kb / 1024;

  // 如果是整数，返回整数字符串
  if (Number.isInteger(mb)) {
    return mb.toString();
  }

  // 否则保留一位小数
  return mb.toFixed(1);
}

/**
 * 格式化文件大小，自动选择合适的单位 (B, KB, MB, GB, TB)
 * 更简洁的实现版本
 * @param bytes - 字节数
 * @returns string - 格式化后的文件大小
 */
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  if (i === 0) return `${bytes}${units[i]}`;

  const size = bytes / Math.pow(1024, i);
  const formatted = Number.isInteger(size) ? size.toString() : size.toFixed(1);

  return `${formatted}${units[i]}`;
}

/**
 * 将URL的文件后缀修改为.mp4
 * @param url 原始URL
 * @returns 修改后的URL
 */
export function convertToMP4Url(url: string): string {
  if (!url) return url;

  // 处理URL中可能包含的查询参数
  const [baseUrl, queryString] = url.split('?');

  // 移除原有后缀（如果存在），并添加.mp4
  const newBaseUrl = baseUrl.replace(/\.[^.]*$/, '') + '.mp4';

  // 如果原URL包含查询参数，则保留
  return queryString ? `${newBaseUrl}?${queryString}` : newBaseUrl;
}

/**
 * 检查视频是否可播放
 * @param videoUrl 视频URL
 * @returns Promise，解析为布尔值，表示视频是否可播放
 */
export function checkVideoPlayable(videoUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    const video = document.createElement('video');

    // 设置一个较短的超时时间，避免长时间等待
    const timeoutId = setTimeout(() => {
      video.remove();
      resolve(false);
    }, 5000); // 5秒超时

    // 监听可以播放事件
    video.oncanplay = () => {
      clearTimeout(timeoutId);
      video.remove();
      resolve(true);
    };

    // 监听错误事件
    video.onerror = () => {
      clearTimeout(timeoutId);
      video.remove();
      resolve(false);
    };

    // 添加时间戳参数避免缓存
    const cacheBusterUrl = `${videoUrl}${videoUrl.includes('?') ? '&' : '?'}_t=${new Date().getTime()}`;
    video.src = cacheBusterUrl;

    // 尝试加载视频元数据
    video.load();
  });
}

/**
 * 检测媒体资源是否可访问（图片、视频）
 * @param url 资源地址
 * @returns Promise<boolean> 是否可访问
 */
export function checkMediaAccessible(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }

    // 简单判断是否为视频
    const isVideo = /\.(mp4|webm|ogg|mov)($|\?)/i.test(url);

    if (isVideo) {
      const video = document.createElement('video');
      // 只要能加载元数据，说明资源存在且可访问
      video.onloadedmetadata = () => resolve(true);
      video.onerror = () => resolve(false);
      video.src = url;
      return;
    }

    // 默认按图片处理
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}
